import {
  House, SquarePen, CalendarClock, Library, Activity, FolderKanban,
  MessageSquare, UserSearch, Bot, CircleHelp, LogOut, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Thanh điều hướng bên trái.
 *
 * Nhãn, thứ tự và biểu tượng lấy từ bản đang chạy trên https://fbmax.net,
 * đọc qua CDP ngày 14/09/2026. Lớp Tailwind cũng là nguyên văn từ DOM thật,
 * không phải viết lại theo cảm nhận.
 */

export type ManHinh =
  | 'tong-quan' | 'tao-bai' | 'lich-dinh-ky' | 'kho-bai-viet'
  | 'tien-trinh' | 'kenh-nhom' | 'binh-luan' | 'tim-kiem-kh' | 'mcp-agent';

interface MucMenu {
  ma: ManHinh;
  nhan: string;
  icon: LucideIcon;
  sapCo?: boolean;
}

const MENU: MucMenu[] = [
  { ma: 'tong-quan',    nhan: 'Tổng quan',    icon: House },
  { ma: 'tao-bai',      nhan: 'Tạo bài',      icon: SquarePen },
  { ma: 'lich-dinh-ky', nhan: 'Lịch định kỳ', icon: CalendarClock },
  { ma: 'kho-bai-viet', nhan: 'Kho bài viết', icon: Library },
  { ma: 'tien-trinh',   nhan: 'Tiến Trình',   icon: Activity },
  { ma: 'kenh-nhom',    nhan: 'Kênh & nhóm',  icon: FolderKanban },
  { ma: 'binh-luan',    nhan: 'Bình luận',    icon: MessageSquare },
  { ma: 'tim-kiem-kh',  nhan: 'Tìm kiếm KH',  icon: UserSearch, sapCo: true },
];

// Lớp nguyên văn từ DOM thật — giữ để giao diện khớp bản cũ.
const MUC_CHUNG =
  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors';
const MUC_DANG_MO =
  'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400';
const MUC_THUONG =
  'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700/50 dark:hover:text-white';
const MUC_DUOI =
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50';

interface Props {
  dangMo: ManHinh;
  doiMan: (m: ManHinh) => void;
  moTrenDienThoai: boolean;
  dongMenu: () => void;
  daDangNhap: boolean;
  onDangXuat: () => void;
}

export function ThanhBen({ dangMo, doiMan, moTrenDienThoai, dongMenu, daDangNhap, onDangXuat }: Props) {
  return (
    <aside
      className={
        'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-slate-200 bg-white ' +
        'transition-transform dark:border-slate-700 dark:bg-slate-800 md:static md:z-auto md:translate-x-0 ' +
        (moTrenDienThoai ? 'translate-x-0' : '-translate-x-full')
      }
    >
      {/* Đầu trang */}
      <div className="flex items-center gap-2 border-b border-slate-200 p-4 dark:border-slate-700">
        <a
          href="/"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-lg font-bold text-white">
            F
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-bold text-slate-900 dark:text-white">FBMax</span>
            <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
              FBI Social Engine
            </span>
          </span>
        </a>
        <button onClick={dongMenu} aria-label="Đóng menu" className="rounded-lg p-2 text-slate-400 md:hidden">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Menu chính */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Không gian làm việc
        </p>
        {MENU.map(({ ma, nhan, icon: Icon, sapCo }) => (
          <button
            key={ma}
            onClick={() => { doiMan(ma); dongMenu(); }}
            className={MUC_CHUNG + ' ' + (dangMo === ma ? MUC_DANG_MO : MUC_THUONG)}
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 text-left">{nhan}</span>
            {sapCo && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                SẮP CÓ
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Chân trang */}
      <div className="space-y-1 border-t border-slate-200 p-3 dark:border-slate-700">
        <a href="/" className={MUC_DUOI + ' font-semibold'}>
          <House className="h-[18px] w-[18px]" />
          Về trang chủ
        </a>
        <button onClick={() => { doiMan('mcp-agent'); dongMenu(); }} className={MUC_DUOI + ' w-full'}>
          <Bot className="h-[18px] w-[18px]" />
          MCP Agent
        </button>
        <button
          disabled
          className="flex w-full cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400"
        >
          <CircleHelp className="h-[18px] w-[18px]" />
          Trợ giúp · Sắp ra mắt
        </button>
        {daDangNhap ? (
          <button onClick={onDangXuat} className={MUC_DUOI + ' text-rose-600 hover:bg-rose-50 dark:text-rose-400'}>
            <LogOut className="h-[18px] w-[18px]" />
            Đăng xuất
          </button>
        ) : (
          <a href="/login?next=%2Fapp%2F" className={MUC_DUOI + ' font-semibold text-slate-700 dark:text-slate-200'}>
            <LogOut className="h-[18px] w-[18px]" />
            Đăng nhập
          </a>
        )}
      </div>
    </aside>
  );
}

export { MENU };
